import { Component, h } from 'preact';
import CSSModules from 'react-css-modules';
import ClassNames from 'classnames';
import styles from '../../css/app.css';
import ReactMarkdown from 'react-markdown';
import TweenLite from 'gsap/TweenLite';
import TimelineLite from 'gsap/TimelineLite';
import { Container, Graphics, Text } from 'pixi.js';
import 'gsap/PixiPlugin';

import { GetSimpleRect } from '../../utils-and-data/utils.js';
import events from '../../utils-and-data/events.js';
import FxLayer from '../partials/fx-layer.js';
import BackgroundGhost from '../animations/background';
import TextFx from '../animations/lighting';

// @TODO: move these into config, maybe?
const eventOptions = {
  passive: false,
  capture: true
};

class Lighting extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.ghosting = false;
    this.initialised = false;
    this.destroyTimer = null;
    this.START_GHOST_TOUCH_THRESHOLD = 10;
  }

  componentWillUpdate(nextProps, nextState) {

    // Allow the webGL layer to follow the carousel's movement:
    if (nextProps.ghostTransX && this.initialised && this.container) {
      this.container.x = nextProps.ghostTransX;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.order !== this.props.order) {
      this.initOrDestroy();
    } else if (prevProps.dimensions.height !== this.props.dimensions.height) {
      this.textRect = GetSimpleRect(this.textRef);
      this.hitzoneRect = GetSimpleRect(this.hitzoneRef);
    }
  }

  componentDidMount() {
    this.initOrDestroy(this.props);
  }

  componentWillUnmount() {
    this.destroyGhost();
  }

  initOrDestroy() {
    if (this.props.order === 'curr'
      && this.initialised === false) {

      this.initialised = true;
      this.startTimer = setTimeout(() => this.initGhost(), 5);
    } else {
      this.destroyGhost();
    }
  }

  showGhost() {
    this.props.resetCarousel();
    console.log('Lighting showGhost()');
    this.ghosting = true;

    // unlock carousel and show ghost text after
    // animation is complete

    this.whiteBg.alpha = 1;
    this.normalText.alpha = 1;

    // hide currentText
    this.normalText.animateOut(() => {});
    TweenLite.delayedCall(.3, () => this.showText());
  }

  showText() {
    this.bg.flickerShow(() => {
      events.emit('setLighting', { mode: 'dark' });
      this.floText.animateIn();
      TweenLite.delayedCall(2, () => this.unlock());
    });
  }

  unlock() {
    console.log('Ghost Light unlock()');
    FxLayer.bounce(() => {
      this.releaseEvents();
      this.props.setCarouselLock(false);
    });
  }

  initGhost() {
    this.captureEvents();
    this.props.resetCarousel();
    this.props.setCarouselLock(true);
    setTimeout(() => {
      this.textRect = GetSimpleRect(this.textRef);
      this.hitzoneRect = GetSimpleRect(this.hitzoneRef);
      this.createGhostScene();
    });
  }

  destroyGhost() {
    clearTimeout(this.startTimer);
    if (!this.initialised) return;

    // MP here
    // TODO: This method is being called multiple times,
    // so we need verify if the object is already destroyed
    if (this.bg) {
      TweenLite.killTweensOf(this.floText);
      TweenLite.killTweensOf(this.normalText);
      this.floText._destroy();
      this.normalText._destroy();
      this.bg._destroy();
    }

    if (this.container) this.cleanup();
    FxLayer.voiceLayer.clearChildren();
    FxLayer.stop();
    this.initialised = false;
    this.ghosting = false;
    events.emit('setLighting', { mode: 'light' });
  }

  cleanup() {

    // loop through all elements appended to scene and remove them
    while (this.container.children[0]) {
      this.container.removeChild(this.container.children[0]);
      this.container.destroy();
    }
  }

  createGhostScene() {
    this.container = new Container();
    this.whiteBg = this.createRect(this.props.dimensions, 0xffffff);
    this.whiteBg.alpha = 0;

    const t = this.drawText(this.props.paragraphs, 0x222222);
    this.normalText = new TextFx(t);
    this.normalText.alpha = 0;

    this.bg = new BackgroundGhost(this.props, 'lighting');
    const f = this.drawText(this.props.ghost.paragraphs, 0xffffff);
    this.floText = new TextFx(f);
    this.floText.alpha = 0;

    this.container.addChild(this.whiteBg);
    this.container.addChild(this.normalText);
    this.container.addChild(this.bg);
    this.container.addChild(this.floText);

    FxLayer.voiceLayer.addChild(this.container);
    FxLayer.start();
  }

  drawText(p, color) {
    let str = '';
    p.map((s, i) => i !== p.length - 1 
      ? str += `${s} \n\n`
      : str += `${s}`);
    const txt = new Text(str, {
      fontFamily: 'Averia Serif Libre',
      fontWeight: 300,
      fontSize: 16,
      fill : color,
      align : color === 0xffffff ? 'center' : 'left',
      wordWrap: true,
      wordWrapWidth: this.textRect.width,
      lineHeight: 25
    });

    let left = color === 0xffffff
      ? (this.props.dimensions.width / 2) - (txt.width / 2)
      : this.textRect.left - this.hitzoneRect.left;
    let top = color === 0xffffff
      ? (this.props.dimensions.height / 2) - (txt.height / 2)
      : this.textRect.top;
    txt.position.x = left;
    txt.position.y = top - this.hitzoneRect.top;
    return txt;
  }

  // createRect is used in a lot of places and should
  // be inside a utils package.
  createRect(size, color) {
    const bg = new Graphics();
    bg.beginFill(color, 1);
    bg.drawRect(0, 0, size.width, size.height);
    bg.endFill()
    return bg;
  }

  // define some custom canvas methods:
  captureEvents() {
    this.hitzoneRef.addEventListener('mousedown', this.onDrawStart, eventOptions);
    this.hitzoneRef.addEventListener('mouseup', this.onDrawEnd, eventOptions);
    this.hitzoneRef.addEventListener('touchstart', this.onDrawStart, eventOptions);
    this.hitzoneRef.addEventListener('touchend', this.onDrawEnd, eventOptions);
  }

  releaseEvents() {
    this.hitzoneRef.removeEventListener('mouseup', this.onDrawEnd, eventOptions);
    this.hitzoneRef.removeEventListener('mousemove', this.onMouseMove, eventOptions);
    this.hitzoneRef.removeEventListener('mousedown', this.onDrawStart, eventOptions);

    this.hitzoneRef.removeEventListener('touchend', this.onDrawEnd, eventOptions);
    this.hitzoneRef.removeEventListener('touchmove', this.onTouchMove, eventOptions);
    this.hitzoneRef.removeEventListener('touchstart', this.onDrawStart, eventOptions);
  }

  stopAndPrevent(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  onDrawStart = (e) => {
    this.oldPos = null;
    this.stopAndPrevent(e);

    this.hitzoneRef.addEventListener('mousemove', this.onMouseMove, eventOptions);
    this.hitzoneRef.addEventListener('touchmove', this.onTouchMove, eventOptions);
  }

  onDrawEnd = (e) => {
    this.stopAndPrevent(e);
    this.hitzoneRef.removeEventListener('mousemove', this.onMouseMove, eventOptions);
    this.hitzoneRef.removeEventListener('touchmove', this.onTouchMove, eventOptions);
  }

  onMouseMove = (e) => {
    this.stopAndPrevent(e);
    let y = e.pageY - this.hitzoneRect.top;
    let x = e.pageX - this.hitzoneRect.left;
    this.onDrawMove(x,y);
  }

  onTouchMove = (e) => {
    this.stopAndPrevent(e);
    let x = e.touches[0].clientX - this.hitzoneRect.left,
      y = e.touches[0].clientY - this.hitzoneRect.top;
    this.onDrawMove(x,y);
  }

  onDrawMove = (x,y) => {
    if (this.complete) return;
    if (!this.oldPos) this.oldPos = { x: x, y: y };

    // Detect if they've dragged enough from the start point
    // to enable the ghost to start:
    if (!this.ghosting) {
      // console.log('detect start of ghost!');
      if (Math.max(
        Math.abs(this.oldPos.x - x),
        Math.abs(this.oldPos.y - y)
      ) >= this.START_GHOST_TOUCH_THRESHOLD) {

        console.log('show ghost!');
        this.showGhost();
      }
    }
  }


  storeRef(type, el) {
    // console.log('store a ref:', el);
    if (el !== null) this[type] = el;
  }

  render() {
    return (
      <div styleName='flickering-lights'
        className={ClassNames('shift-ghost', this.props.order)}
        ref={h => this.storeRef('hitzoneRef', h)}>

        <div className='inner'
          ref={t => this.storeRef('textRef', t)}>

          {this.props.paragraphs.map((text,i) => {
            return <ReactMarkdown source={text} />
          })}
        </div>
      </div>
    )
  }
}

// Apply 'react-css-modules' to this react component:
export default CSSModules(Lighting, styles);



// WEBPACK FOOTER //
// ./app/frontend/components/panels/ghost-lighting.jsx